// The inspector's Section tab, in Flowmark's layout: a head row (type icon,
// type name, a "Hidden" chip, then hide, duplicate and delete), the Form group
// for a Form section, the section's own fields, and the Style group (closed
// by default). SectionTabEmpty is what the tab shows with nothing selected.
import type { PageFormRef, Section } from '../model/pages';
import { SECTION_LABEL } from '../model/pages';
import type { CrmFormSummary } from '../api';
import { CopyIcon, EyeIcon, EyeOffIcon, MousePointerClickIcon, TrashIcon } from '../icons';
import { SECTION_ICON } from './AddSection';
import { SectionFields, SectionStyleFields } from './SectionFields';
import { FormGroup } from './FormGroup';
import { FormFieldControls } from './FormFieldControls';
import { BTN, ICON_BTN, ICON_BTN_DANGER, INSPECTOR_SCOPE } from './InspectorKit';

export interface SectionTabProps {
  /** The selected section (or the shared footer). */
  section: Section;
  /** The selection is the page's shared footer: no hide, no duplicate, no Style. */
  isFooter?: boolean;
  /** Patches the selected section (PageEditor's updateSelected). */
  onChange: (p: Partial<Section>) => void;
  /** The page has more than one step (changes a hint on the CTA button). */
  multiStep?: boolean;
  /** The section is on the funnel's last step or the thank-you (Form section defaults). */
  lastStep?: boolean;
  /** Hide or show the section. Defaults to toggling `hidden` through onChange. */
  onToggleHidden?: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onCopyStyle?: () => void;
  onPasteStyle?: () => void;
  onResetStyle?: () => void;
  canPasteStyle?: boolean;

  // A Form section only.
  /** Live and draft CRM forms (null while loading). */
  forms?: CrmFormSummary[] | null;
  /** The page's embedded form (page.form_id). */
  formId?: number | null;
  /** The linked form as the page row has it (page.form). */
  linkedForm?: PageFormRef | null;
  onFormChange?: (formId: number | null) => void;
  onNewForm?: () => void;
  formBusy?: boolean;
  /** The page is live (the draft-form note says so). */
  pageLive?: boolean;
}

const noop = () => {};

export function SectionTab({
  section, isFooter = false, onChange, multiStep, lastStep, onToggleHidden, onDuplicate, onDelete,
  onCopyStyle, onPasteStyle, onResetStyle, canPasteStyle,
  forms = null, formId = null, linkedForm = null, onFormChange = noop, onNewForm = noop, formBusy = false, pageLive = false,
}: SectionTabProps) {
  const hidden = !!section.hidden;
  const label = SECTION_LABEL[section.type] ?? 'Section';
  // Shown again: drop the key, so a shown section looks exactly like one that
  // was never hidden.
  const toggleHidden = onToggleHidden ?? (() => onChange({ hidden: hidden ? undefined : true }));
  return (
    <div className={`min-w-0 pb-8 ${INSPECTOR_SCOPE}`} data-inspector="section">
      <div className="flex min-w-0 items-center gap-2.5 px-4 pb-3 pt-3.5">
        <span aria-hidden className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-surface2 text-text/80 [&_svg]:!h-[15px] [&_svg]:!w-[15px]">
          {SECTION_ICON[section.type]}
        </span>
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <h2 className="truncate text-base font-semibold tracking-[-0.01em] text-text">{label}</h2>
          {hidden && (
            <span className="inline-flex h-5 shrink-0 items-center rounded-full bg-surface2 px-2 text-xs font-medium text-textDim">Hidden</span>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-0.5">
          {!isFooter && (
            <button type="button" className={ICON_BTN} onClick={toggleHidden}
              aria-label={hidden ? 'Show section' : 'Hide section'} title={hidden ? 'Show on the page' : 'Hide from the page'}>
              {hidden ? <EyeOffIcon size={15} /> : <EyeIcon size={15} />}
            </button>
          )}
          {!isFooter && (
            <button type="button" className={ICON_BTN} onClick={onDuplicate} aria-label="Duplicate section" title="Duplicate">
              <CopyIcon size={15} />
            </button>
          )}
          <button type="button" className={ICON_BTN_DANGER} onClick={onDelete}
            aria-label={isFooter ? 'Remove footer' : 'Delete section'} title={isFooter ? 'Remove footer' : 'Delete'}>
            <TrashIcon size={15} />
          </button>
        </div>
      </div>
      {section.type === 'form' && (
        <FormGroup section={section} forms={forms} formId={formId} linkedForm={linkedForm} onFormChange={onFormChange}
          onNewForm={onNewForm} formBusy={formBusy} pageLive={pageLive} onChange={onChange} lastStep={lastStep} />
      )}
      <SectionFields section={section} onChange={onChange} multiStep={multiStep} />
      {section.type === 'form' && <FormFieldControls section={section} formId={formId} onChange={onChange} />}
      {!isFooter && <SectionStyleFields section={section} onChange={onChange}
        onCopyStyle={onCopyStyle} onPasteStyle={onPasteStyle} onResetStyle={onResetStyle} canPasteStyle={canPasteStyle} />}
    </div>
  );
}

/** The Section tab with nothing selected: a short hint and a way to the Page tab. */
export function SectionTabEmpty({ onPageDesign }: { onPageDesign: () => void }) {
  return (
    <div className="grid min-w-0 justify-items-center gap-2 px-5 py-12 text-center" data-inspector="section-empty">
      <MousePointerClickIcon size={20} className="text-textDim" />
      <h2 className="text-base font-semibold tracking-[-0.01em] text-text">Select a section</h2>
      <p className="max-w-[256px] text-sm text-textDim">Click any part of the page to edit it, or change how the whole page looks.</p>
      <button type="button" className={`${BTN} mt-2`} onClick={onPageDesign}>Page design</button>
    </div>
  );
}
